import { useCallback } from 'react';
import { toast } from 'sonner';
import { useFeature } from '@/hooks/useFeature';
import type { XlsxSheet } from '@/lib/xlsxExport';

interface RunXlsxOptions {
  /** Brand color hex (e.g. '#10B981') for the header fill. */
  brandColor?: string;
  /** Message shown when there is nothing to export. */
  emptyMessage?: string;
  /** Toast shown on success. */
  successMessage?: string;
}

/**
 * Shared handler for XLSX exports across pages. Gates on the PRO `xls_export`
 * feature (opening the paywall for free users), lazily loads the exceljs-backed
 * exporter only when actually exporting, and surfaces success/error toasts.
 *
 * `build` returns the sheet definitions and is only invoked once access is
 * granted — pass a closure over the current (filtered or selected) data.
 */
export function useXlsxExport() {
  const xlsExport = useFeature('xls_export');

  const exportXlsx = useCallback(
    async (
      build: () => XlsxSheet[] | Promise<XlsxSheet[]>,
      domain: string,
      options: RunXlsxOptions = {},
    ): Promise<boolean> => {
      if (!xlsExport.require()) return false;

      try {
        const sheets = await build();
        const hasRows = sheets.some((sheet) => sheet.rows.length > 0);
        if (!hasRows) {
          toast.warning(options.emptyMessage ?? 'Nada para exportar');
          return false;
        }

        const { exportToXlsx, buildXlsxFilename } = await import('@/lib/xlsxExport');
        await exportToXlsx(sheets, buildXlsxFilename(domain), options.brandColor);
        toast.success(options.successMessage ?? 'Exportado com sucesso!');
        return true;
      } catch (error) {
        console.error('[useXlsxExport] Falha ao exportar XLSX:', error);
        toast.error('Erro ao exportar arquivo');
        return false;
      }
    },
    [xlsExport],
  );

  return { exportXlsx, allowed: xlsExport.allowed };
}
